// src/components/FeedbackManagement.jsx

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  StarIcon as StarIconSolid,
  PaperAirplaneIcon,
  ChatBubbleBottomCenterTextIcon,
  UserGroupIcon,
} from '@heroicons/react/24/solid';
import { StarIcon as StarIconOutline } from '@heroicons/react/24/outline';

// Dummy data for demonstration
const DUMMY_EMPLOYEES = [
  { id: 'emp1', name: 'Alice Johnson' },
  { id: 'emp2', name: 'Bob Smith' },
  { id: 'emp3', name: 'Charlie Brown' },
];

const DUMMY_FEEDBACK = [
  {
    id: 'f1',
    employeeName: 'Alice Johnson',
    type: 'Performance Review',
    content: 'Alice has shown significant improvement in project management. Her attention to detail is commendable.',
    rating: 5,
    date: '2025-09-20',
  },
  {
    id: 'f2',
    employeeName: 'Bob Smith',
    type: 'General Feedback',
    content: 'Bob is a great team player and always willing to help others. Needs to work on time management for deadlines.',
    rating: 3,
    date: '2025-09-18',
  },
  {
    id: 'f3',
    employeeName: 'Charlie Brown',
    type: 'Skill Development',
    content: 'Charlie completed the advanced React course and is applying new knowledge to the frontend. Excellent progress.',
    rating: 4,
    date: '2025-09-15',
  },
];

const FeedbackManagement = () => {
  const [feedback, setFeedback] = useState(DUMMY_FEEDBACK);
  const [formState, setFormState] = useState({
    employeeId: '',
    type: 'Performance Review',
    content: '',
    rating: 0,
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormState({ ...formState, [name]: value });
  };

  const handleRatingChange = (newRating) => {
    setFormState({ ...formState, rating: newRating });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formState.employeeId || !formState.content || formState.rating === 0) {
      alert('Please fill out all fields and provide a rating.');
      return;
    }

    const newFeedback = {
      id: `f${feedback.length + 1}`,
      employeeName: DUMMY_EMPLOYEES.find(emp => emp.id === formState.employeeId)?.name,
      type: formState.type,
      content: formState.content,
      rating: formState.rating,
      date: new Date().toISOString().slice(0, 10),
    };

    setFeedback([newFeedback, ...feedback]);
    setFormState({
      employeeId: '',
      type: 'Performance Review',
      content: '',
      rating: 0,
    });
  };

  return (
    <div className="p-6 md:p-10 bg-gray-50 min-h-screen">
      <header className="mb-8">
        <h1 className="text-3xl md:text-4xl font-extrabold text-gray-900 flex items-center">
          <ChatBubbleBottomCenterTextIcon className="h-8 w-8 text-teal-500 mr-2" />
          Feedback Management
        </h1>
        <p className="mt-2 text-lg text-gray-600">
          Provide constructive feedback to your team members and review past evaluations.
        </p>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Feedback Submission Form */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="bg-white p-6 rounded-xl shadow-lg border border-gray-200 h-fit"
        >
          <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
            <PaperAirplaneIcon className="h-6 w-6 text-indigo-500 mr-2" />
            Submit New Feedback
          </h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="employeeId" className="block text-sm font-medium text-gray-700">
                Employee
              </label>
              <select
                id="employeeId"
                name="employeeId"
                value={formState.employeeId}
                onChange={handleInputChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 text-sm"
                required
              >
                <option value="">Select an employee</option>
                {DUMMY_EMPLOYEES.map((emp) => (
                  <option key={emp.id} value={emp.id}>
                    {emp.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="type" className="block text-sm font-medium text-gray-700">
                Feedback Type
              </label>
              <select
                id="type"
                name="type"
                value={formState.type}
                onChange={handleInputChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 text-sm"
              >
                <option>Performance Review</option>
                <option>General Feedback</option>
                <option>Skill Development</option>
              </select>
            </div>

            <div>
              <label htmlFor="content" className="block text-sm font-medium text-gray-700">
                Feedback Content
              </label>
              <textarea
                id="content"
                name="content"
                rows="4"
                value={formState.content}
                onChange={handleInputChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 text-sm"
                placeholder="Enter your feedback here..."
                required
              ></textarea>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Rating
              </label>
              <div className="flex items-center mt-1 space-x-1">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    type="button"
                    key={star}
                    onClick={() => handleRatingChange(star)}
                    className="p-1 text-yellow-400 focus:outline-none transition-transform duration-200 hover:scale-110"
                  >
                    {star <= formState.rating ? (
                      <StarIconSolid className="h-6 w-6" />
                    ) : (
                      <StarIconOutline className="h-6 w-6" />
                    )}
                  </button>
                ))}
              </div>
            </div>

            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              type="submit"
              className="w-full py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 transition-colors"
            >
              Submit Feedback
            </motion.button>
          </form>
        </motion.div>

        {/* Feedback History */}
        <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-200">
          <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
            <UserGroupIcon className="h-6 w-6 text-purple-500 mr-2" />
            Feedback History
          </h2>
          <div className="space-y-4 max-h-[600px] overflow-y-auto pr-2 custom-scrollbar">
            <AnimatePresence>
              {feedback.length > 0 ? (
                feedback.map((item) => (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.3 }}
                    className="p-4 bg-gray-50 rounded-lg border border-gray-200"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="font-bold text-sm text-gray-900">{item.employeeName}</div>
                      <span className="text-xs text-gray-500">{item.date}</span>
                    </div>
                    <div className="flex items-center mb-2">
                      <span className="text-xs font-medium text-gray-600 mr-2">{item.type}</span>
                      <div className="flex text-yellow-400">
                        {[...Array(5)].map((_, i) => (
                          <StarIconSolid key={i} className={`h-4 w-4 ${i < item.rating ? 'text-yellow-400' : 'text-gray-300'}`} />
                        ))}
                      </div>
                    </div>
                    <p className="text-sm text-gray-700 leading-relaxed">{item.content}</p>
                  </motion.div>
                ))
              ) : (
                <div className="text-center text-gray-500 p-8">No feedback submitted yet.</div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeedbackManagement;